import React, { useState, useEffect } from 'react';
import { Users, X, ArrowRight, Check, AlertTriangle, Loader2, Search } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import './MergePatientsModal.css';

const MergePatientsModal = ({ isOpen, onClose, onMergeSuccess }) => {
  const { addNotification } = useNotifications();
  const [step, setStep] = useState(1);

  // Patient states
  const [searchSource, setSearchSource] = useState('');
  const [sourceResults, setSourceResults] = useState([]);
  const [sourcePatient, setSourcePatient] = useState(null);

  const [searchDest, setSearchDest] = useState('');
  const [destResults, setDestResults] = useState([]);
  const [destinationPatient, setDestinationPatient] = useState(null);

  // Status states
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [merging, setMerging] = useState(false);
  const [finished, setFinished] = useState(false);

  // Stats for confirmation step
  const [stats, setStats] = useState({ appointments: 0, evolutions: 0 });

  // Active Mode (manual wizard vs automatic scanner)
  const [activeMode, setActiveMode] = useState('scan'); // 'scan' | 'manual'

  // Automatic scanner states
  const [loadingScan, setLoadingScan] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [mergingGroupId, setMergingGroupId] = useState(null);
  const [mergingAll, setMergingAll] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset state on close
      setActiveMode('scan');
      setStep(1);
      setSearchSource('');
      setSourceResults([]);
      setSourcePatient(null);
      setSearchDest('');
      setDestResults([]);
      setDestinationPatient(null);
      setFinished(false);
      setStats({ appointments: 0, evolutions: 0 });
      setDuplicateGroups([]);
    } else {
      runDuplicateScan();
    }
  }, [isOpen]);

  const runDuplicateScan = async () => {
    setLoadingScan(true);
    try {
      // Fetch all patients (id, full_name, dni, phone, email, insurance_provider, medical_notes, created_at)
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        setDuplicateGroups([]);
        return;
      }

      // Group patients by clean DNI or normalized Full Name
      const groupsMap = {};

      data.forEach(patient => {
        const cleanDni = (patient.dni || '').replace(/[^0-9]/g, '').trim();
        const normName = (patient.full_name || '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase();

        // 1. Group by DNI if present (min 6 digits)
        if (cleanDni.length >= 6) {
          const dniKey = `dni:${cleanDni}`;
          if (!groupsMap[dniKey]) {
            groupsMap[dniKey] = {
              key: dniKey,
              type: 'DNI',
              matchValue: patient.dni,
              patients: []
            };
          }
          groupsMap[dniKey].patients.push(patient);
        }

        // 2. Group by Name if name exists (min 4 letters)
        if (normName.length >= 4) {
          const nameKey = `name:${normName}`;
          if (!groupsMap[nameKey]) {
            groupsMap[nameKey] = {
              key: nameKey,
              type: 'Nombre',
              matchValue: patient.full_name,
              patients: []
            };
          }
          // Avoid duplicate inclusion if already added under exact same name group
          if (!groupsMap[nameKey].patients.some(p => p.id === patient.id)) {
            groupsMap[nameKey].patients.push(patient);
          }
        }
      });

      // Filter groups that have >= 2 patients
      const rawGroups = Object.values(groupsMap).filter(g => g.patients.length >= 2);

      // Deduplicate patients across overlapping DNI/Name groups
      const resultGroups = [];
      const seenGroupKeys = new Set();

      rawGroups.forEach(group => {
        if (seenGroupKeys.has(group.key)) return;
        seenGroupKeys.add(group.key);

        // Sort patients in group: prefer patient with DNI, phone, email, or oldest created_at as main
        const sorted = [...group.patients].sort((a, b) => {
          let scoreA = (a.dni ? 10 : 0) + (a.phone ? 5 : 0) + (a.email ? 3 : 0);
          let scoreB = (b.dni ? 10 : 0) + (b.phone ? 5 : 0) + (b.email ? 3 : 0);
          if (scoreA !== scoreB) return scoreB - scoreA;
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        });

        const mainPatient = sorted[0];
        const duplicates = sorted.slice(1);

        resultGroups.push({
          id: group.key,
          type: group.type,
          matchValue: group.matchValue,
          mainPatient,
          duplicates
        });
      });

      setDuplicateGroups(resultGroups);
    } catch (err) {
      console.error('Error scanning duplicates:', err);
      addNotification('Error al escanear pacientes duplicados: ' + err.message, 'error');
    } finally {
      setLoadingScan(false);
    }
  };

  const mergeSingleGroup = async (group) => {
    setMergingGroupId(group.id);
    try {
      const dest = group.mainPatient;
      for (const src of group.duplicates) {
        // Patch fields
        const patchData = {};
        if (!dest.dni && src.dni) patchData.dni = src.dni;
        if (!dest.phone && src.phone) patchData.phone = src.phone;
        if (!dest.email && src.email) patchData.email = src.email;
        if (!dest.birthdate && src.birthdate) patchData.birthdate = src.birthdate;
        if (!dest.insurance_provider && src.insurance_provider) patchData.insurance_provider = src.insurance_provider;
        
        if (src.medical_notes) {
          patchData.medical_notes = dest.medical_notes
            ? `${dest.medical_notes}\n\n[Nota importada por fusión]: ${src.medical_notes}`
            : src.medical_notes;
        }

        if (Object.keys(patchData).length > 0) {
          const { error: patchError } = await supabase.from('patients').update(patchData).eq('id', dest.id);
          if (patchError) console.warn('Patch error on dest patient:', patchError);
        }

        // Transfer medical evolutions
        const { error: evErr } = await supabase.from('medical_evolutions').update({ patient_id: dest.id }).eq('patient_id', src.id);
        if (evErr) console.warn('Medical evolutions update error:', evErr);

        // Transfer appointments (by patient_id, and also by patient_dni, clean DNI, or patient_name)
        const updatePayload = {
          patient_id: dest.id,
          patient_dni: dest.dni || null,
          patient_name: dest.full_name,
          patient_email: dest.email || null,
          patient_phone: dest.phone || null
        };

        // 1. Reassign by exact patient_id
        const { error: appErr1 } = await supabase.from('appointments').update(updatePayload).eq('patient_id', src.id);
        if (appErr1) console.warn('App error 1:', appErr1);

        // 2. Reassign by raw DNI & cleaned numeric DNI
        if (src.dni) {
          await supabase.from('appointments').update(updatePayload).eq('patient_dni', src.dni);
          const srcCleanDni = src.dni.replace(/[^0-9]/g, '');
          if (srcCleanDni) {
            await supabase.from('appointments').update(updatePayload).eq('patient_dni', srcCleanDni);
            await supabase.from('appointments').update(updatePayload).ilike('patient_dni', `%${srcCleanDni}%`);
          }
        }

        // 3. Reassign by patient_name
        if (src.full_name) {
          await supabase.from('appointments').update(updatePayload).ilike('patient_name', src.full_name.trim());
        }

        // 4. Force disassociate any remaining appointments referencing src.id (set patient_id to dest.id)
        await supabase.from('appointments').update({ patient_id: dest.id }).eq('patient_id', src.id);

        // Delete source patient
        let { error: deleteError } = await supabase
          .from('patients')
          .delete()
          .eq('id', src.id);

        // If deletion fails due to remaining appointments, set patient_id to null on orphan appointments as fallback before retrying delete
        if (deleteError && (deleteError.message?.includes('appointments_patient_id_fkey') || deleteError.details?.includes('appointments_patient_id_fkey'))) {
          await supabase.from('appointments').update({ patient_id: dest.id }).eq('patient_id', src.id);
          const { error: retryDeleteErr } = await supabase.from('patients').delete().eq('id', src.id);
          deleteError = retryDeleteErr;
        }

        if (deleteError) {
          console.error('Delete error for patient id:', src.id, deleteError);
          throw new Error(`No se pudo eliminar el registro duplicado ID ${src.id}: ${deleteError.message || deleteError.details || 'Violación de restricción de base de datos'}`);
        }
      }

      addNotification(`Grupo "${group.matchValue}" unificado con éxito`, 'success');
      // Remove group from list
      setDuplicateGroups(prev => prev.filter(g => g.id !== group.id));
      if (onMergeSuccess) onMergeSuccess();
    } catch (err) {
      console.error('Error merging group:', err);
      addNotification('Error al unificar grupo: ' + err.message, 'error');
    } finally {
      setMergingGroupId(null);
    }
  };

  const mergeAllGroups = async () => {
    if (duplicateGroups.length === 0) return;
    setMergingAll(true);
    try {
      for (const group of [...duplicateGroups]) {
        await mergeSingleGroup(group);
      }
      addNotification('Todos los pacientes duplicados han sido unificados con éxito', 'success');
      runDuplicateScan();
    } catch (err) {
      console.error('Error merging all groups:', err);
    } finally {
      setMergingAll(false);
    }
  };

  useEffect(() => {
    if (step === 3 && sourcePatient && destinationPatient) {
      fetchSourceStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const fetchSourceStats = async () => {
    try {
      // Get evolutions count
      const { count: evCount, error: evErr } = await supabase
        .from('medical_evolutions')
        .select('*', { count: 'exact', head: true })
        .eq('patient_id', sourcePatient.id);

      if (evErr) throw evErr;

      // Get appointments count
      let appQuery = supabase
        .from('appointments')
        .select('*', { count: 'exact', head: true });

      if (sourcePatient.dni) {
        appQuery = appQuery.eq('patient_dni', sourcePatient.dni);
      } else {
        appQuery = appQuery.eq('patient_name', sourcePatient.full_name);
      }

      const { count: appCount, error: appErr } = await appQuery;
      if (appErr) throw appErr;

      setStats({
        evolutions: evCount || 0,
        appointments: appCount || 0
      });
    } catch (err) {
      console.error('Error fetching source stats:', err);
    }
  };

  if (!isOpen) return null;

  const handleSearchSource = async (e) => {
    if (e) e.preventDefault();
    const cleanRaw = searchSource.replace(/[,()"\\]/g, ' ').trim();
    if (!cleanRaw) return;
    setLoadingSearch(true);
    try {
      const terms = cleanRaw.split(/\s+/).filter(Boolean);
      const andName = `and(${terms.map(t => `full_name.ilike.%${t}%`).join(',')})`;
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .or(`${andName},dni.ilike.%${cleanRaw}%`)
        .limit(10);
      if (error) throw error;
      setSourceResults(data || []);
    } catch (err) {
      addNotification('Error al buscar paciente origen', 'error');
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSearchDest = async (e) => {
    if (e) e.preventDefault();
    const cleanRaw = searchDest.replace(/[,()"\\]/g, ' ').trim();
    if (!cleanRaw) return;
    setLoadingSearch(true);
    try {
      const terms = cleanRaw.split(/\s+/).filter(Boolean);
      const andName = `and(${terms.map(t => `full_name.ilike.%${t}%`).join(',')})`;
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .or(`${andName},dni.ilike.%${cleanRaw}%`)
        .neq('id', sourcePatient.id)
        .limit(10);
      if (error) throw error;
      setDestResults(data || []);
    } catch (err) {
      addNotification('Error al buscar paciente destino', 'error');
    } finally {
      setLoadingSearch(false);
    }
  };

  const executeMerge = async () => {
    if (!sourcePatient || !destinationPatient) return;
    setMerging(true);
    try {
      // 1. Prepare fields to patch on Destination Patient (smart merge empty fields)
      const patchData = {};
      if (!destinationPatient.dni && sourcePatient.dni) patchData.dni = sourcePatient.dni;
      if (!destinationPatient.phone && sourcePatient.phone) patchData.phone = sourcePatient.phone;
      if (!destinationPatient.email && sourcePatient.email) patchData.email = sourcePatient.email;
      if (!destinationPatient.birthdate && sourcePatient.birthdate) patchData.birthdate = sourcePatient.birthdate;
      if (!destinationPatient.insurance_provider && sourcePatient.insurance_provider) patchData.insurance_provider = sourcePatient.insurance_provider;
      
      // Concatenate medical notes if both exist
      if (sourcePatient.medical_notes) {
        patchData.medical_notes = destinationPatient.medical_notes
          ? `${destinationPatient.medical_notes}\n\n[Nota importada por fusión]: ${sourcePatient.medical_notes}`
          : sourcePatient.medical_notes;
      }

      if (Object.keys(patchData).length > 0) {
        const { error: patchError } = await supabase
          .from('patients')
          .update(patchData)
          .eq('id', destinationPatient.id);
        
        if (patchError) throw patchError;
      }

      // 2. Transfer Medical Evolutions
      const { error: evTransferError } = await supabase
        .from('medical_evolutions')
        .update({ patient_id: destinationPatient.id })
        .eq('patient_id', sourcePatient.id);

      if (evTransferError) console.warn('Medical evolutions transfer error:', evTransferError);

      // 3. Transfer Appointments (by patient_id, patient_dni, clean DNI, and patient_name)
      const appPayload = {
        patient_id: destinationPatient.id,
        patient_dni: destinationPatient.dni || null,
        patient_name: destinationPatient.full_name,
        patient_email: destinationPatient.email || null,
        patient_phone: destinationPatient.phone || null
      };

      await supabase.from('appointments').update(appPayload).eq('patient_id', sourcePatient.id);

      if (sourcePatient.dni) {
        await supabase.from('appointments').update(appPayload).eq('patient_dni', sourcePatient.dni);
        const srcCleanDni = sourcePatient.dni.replace(/[^0-9]/g, '');
        if (srcCleanDni) {
          await supabase.from('appointments').update(appPayload).eq('patient_dni', srcCleanDni);
          await supabase.from('appointments').update(appPayload).ilike('patient_dni', `%${srcCleanDni}%`);
        }
      }

      if (sourcePatient.full_name) {
        await supabase.from('appointments').update(appPayload).ilike('patient_name', sourcePatient.full_name.trim());
      }

      // 4. Force disassociate any remaining appointments referencing sourcePatient.id
      await supabase.from('appointments').update({ patient_id: destinationPatient.id }).eq('patient_id', sourcePatient.id);

      // Delete Source Patient
      let { error: deleteError } = await supabase
        .from('patients')
        .delete()
        .eq('id', sourcePatient.id);

      if (deleteError && (deleteError.message?.includes('appointments_patient_id_fkey') || deleteError.details?.includes('appointments_patient_id_fkey'))) {
        await supabase.from('appointments').update({ patient_id: destinationPatient.id }).eq('patient_id', sourcePatient.id);
        const { error: retryDeleteErr } = await supabase.from('patients').delete().eq('id', sourcePatient.id);
        deleteError = retryDeleteErr;
      }

      if (deleteError) {
        console.error('Delete error in manual merge:', deleteError);
        throw new Error(`No se pudo eliminar la ficha duplicada: ${deleteError.message || deleteError.details}`);
      }

      // 5. Complete wizard
      setFinished(true);
      addNotification('Fusión completada con éxito', 'success');
      if (onMergeSuccess) onMergeSuccess();
    } catch (err) {
      console.error(err);
      addNotification('Error al fusionar pacientes: ' + err.message, 'error');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="merge-overlay" onClick={onClose}>
      <div className="merge-container glass-modal-premium" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="merge-header">
          <div className="title-wrapper">
            <Users size={20} className="icon-green" />
            <h2>Unificar Pacientes Repetidos</h2>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div className="merge-mode-selector">
              <button 
                className={`mode-tab-btn ${activeMode === 'scan' ? 'active' : ''}`}
                onClick={() => setActiveMode('scan')}
              >
                Escáner Automático
              </button>
              <button 
                className={`mode-tab-btn ${activeMode === 'manual' ? 'active' : ''}`}
                onClick={() => setActiveMode('manual')}
              >
                Fusión Manual (Paso a Paso)
              </button>
            </div>
            <button className="btn-close-p" onClick={onClose}><X size={24} /></button>
          </div>
        </div>

        {/* AUTOMATIC SCANNER MODE */}
        {activeMode === 'scan' && (
          <div className="merge-body" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div className="scan-top-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.2)', padding: '12px 18px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div>
                <h4 style={{ margin: 0, color: 'white', fontSize: '1.05rem' }}>Búsqueda de Coincidencias en la Base de Datos</h4>
                <p style={{ margin: '4px 0 0 0', color: '#94a3b8', fontSize: '0.85rem' }}>
                  {loadingScan ? 'Escaneando pacientes duplicados...' : `Se detectaron ${duplicateGroups.length} grupo(s) de pacientes duplicados.`}
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  className="btn-scan-refresh"
                  onClick={runDuplicateScan} 
                  disabled={loadingScan || mergingAll}
                  style={{ background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.15)', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}
                >
                  {loadingScan ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Re-escanear
                </button>

                {duplicateGroups.length > 0 && (
                  <button 
                    className="btn-merge-all"
                    onClick={mergeAllGroups}
                    disabled={loadingScan || mergingAll || mergingGroupId !== null}
                    style={{ background: '#10b981', color: '#0d111c', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 700 }}
                  >
                    {mergingAll ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} UNIFICAR TODOS LOS GRUPOS
                  </button>
                )}
              </div>
            </div>

            <div className="duplicate-groups-scroll" style={{ overflowY: 'auto', maxHeight: '55vh', paddingRight: '5px' }}>
              {loadingScan ? (
                <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                  <Loader2 size={32} className="animate-spin" style={{ color: '#00f2fe' }} />
                  <span>Analizando pacientes registrados...</span>
                </div>
              ) : duplicateGroups.length === 0 ? (
                <div style={{ padding: '40px', textAlign: 'center', background: 'rgba(16, 185, 129, 0.05)', borderRadius: '12px', border: '1px dashed rgba(16, 185, 129, 0.3)', color: '#a7f3d0' }}>
                  <Check size={36} style={{ marginBottom: '8px', color: '#10b981' }} />
                  <h3 style={{ margin: '0 0 6px 0', fontSize: '1.1rem' }}>¡No se detectaron duplicados!</h3>
                  <p style={{ margin: 0, fontSize: '0.88rem', color: '#94a3b8' }}>
                    Todos los pacientes registrados tienen DNI y Nombres únicos. Si deseas fusionar fichas manualmente, usa la pestaña "Fusión Manual".
                  </p>
                </div>
              ) : (
                duplicateGroups.map((group) => (
                  <div 
                    key={group.id} 
                    style={{ background: 'rgba(15, 23, 42, 0.6)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', padding: '14px 18px', marginBottom: '12px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ background: group.type === 'DNI' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)', color: group.type === 'DNI' ? '#fca5a5' : '#fcd34d', padding: '3px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 700 }}>
                          Coincidencia por {group.type}: {group.matchValue}
                        </span>
                        <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>({group.duplicates.length + 1} fichas encontradas)</span>
                      </div>

                      <button 
                        onClick={() => mergeSingleGroup(group)}
                        disabled={mergingGroupId === group.id || mergingAll}
                        style={{ background: 'rgba(0, 242, 254, 0.15)', color: '#00f2fe', border: '1px solid rgba(0, 242, 254, 0.3)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
                      >
                        {mergingGroupId === group.id ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Unificar Este Grupo
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      {/* Main */}
                      <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '8px', padding: '10px 12px' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#34d399', textTransform: 'uppercase', marginBottom: '4px' }}>Ficha Principal (Se conserva)</div>
                        <div style={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>{group.mainPatient.full_name?.toUpperCase()}</div>
                        <div style={{ color: '#94a3b8', fontSize: '0.8rem', marginTop: '4px' }}>
                          DNI: {group.mainPatient.dni || 'Sin DNI'} | Cel: {group.mainPatient.phone || 'Sin Cel'} | Os: {group.mainPatient.insurance_provider || 'Particular'}
                        </div>
                      </div>

                      {/* Duplicates */}
                      <div style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: '8px', padding: '10px 12px' }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fca5a5', textTransform: 'uppercase', marginBottom: '4px' }}>Fichas Duplicadas (Se absorberán)</div>
                        {group.duplicates.map(d => (
                          <div key={d.id} style={{ marginBottom: '6px' }}>
                            <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '0.88rem' }}>{d.full_name?.toUpperCase()}</div>
                            <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                              DNI: {d.dni || 'Sin DNI'} | Cel: {d.phone || 'Sin Cel'}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* MANUAL STEP BY STEP MODE */}
        {activeMode === 'manual' && (
          <>
            {/* Steps Indicator */}
            {!finished && (
              <div className="merge-steps-bar">
                <div className={`step-indicator ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                  <div className="circle">1</div>
                  <span>Paciente Duplicado</span>
                </div>
                <div className="line"></div>
                <div className={`step-indicator ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                  <div className="circle">2</div>
                  <span>Paciente Principal</span>
                </div>
                <div className="line"></div>
                <div className={`step-indicator ${step >= 3 ? 'active' : ''}`}>
                  <div className="circle">3</div>
                  <span>Confirmar Fusión</span>
                </div>
              </div>
            )}

        {/* Body content */}
        <div className="merge-body">
          {finished ? (
            <div className="merge-success-state">
              <div className="success-circle">
                <Check size={48} />
              </div>
              <h3>¡Fusión Completada Exitosamente!</h3>
              <p>
                Los registros de <strong>{sourcePatient?.full_name?.toUpperCase()}</strong> se han unificado en la ficha de <strong>{destinationPatient?.full_name?.toUpperCase()}</strong>.
              </p>
              <span className="success-subtext">La ficha duplicada ha sido eliminada y todas las notas clínicas y turnos han sido reasignados de forma segura.</span>
              <button className="btn-close-wizard" onClick={onClose}>CERRAR ASISTENTE</button>
            </div>
          ) : (
            <>
              {/* STEP 1: Select Source Patient (Duplicate to be deleted) */}
              {step === 1 && (
                <div className="wizard-step">
                  <div className="step-instructions">
                    <h3>Paso 1: Selecciona el Paciente Duplicado (Se eliminará)</h3>
                    <p>Busca y selecciona la ficha que contiene datos duplicados o incorrectos. Esta ficha **desaparecerá** y sus registros serán transferidos al paciente principal.</p>
                  </div>

                  <form onSubmit={handleSearchSource} className="wizard-search-form">
                    <div className="search-input-wrapper">
                      <input 
                        type="text" 
                        placeholder="Buscar por Nombre o DNI de la ficha duplicada..."
                        value={searchSource}
                        onChange={(e) => setSearchSource(e.target.value)}
                        autoFocus
                      />
                      <button type="submit" className="btn-search-trigger" disabled={loadingSearch || !searchSource.trim()}>
                        {loadingSearch ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                      </button>
                    </div>
                  </form>

                  {sourcePatient ? (
                    <div className="selected-patient-preview source card">
                      <div className="preview-badge red">FICHA DUPLICADA SELECCIONADA</div>
                      <h4>{sourcePatient.full_name?.toUpperCase()}</h4>
                      <p>DNI: {sourcePatient.dni || 'Sin Documento'} | Celular: {sourcePatient.phone || 'Sin Celular'}</p>
                      <button className="btn-change-selection" onClick={() => setSourcePatient(null)}>Cambiar Selección</button>
                    </div>
                  ) : (
                    <div className="search-results-box">
                      {sourceResults.length === 0 ? (
                        <div className="empty-results-text">Usa el buscador superior para encontrar pacientes.</div>
                      ) : (
                        <div className="search-list">
                          {sourceResults.map(p => (
                            <div key={p.id} className="search-list-item" onClick={() => setSourcePatient(p)}>
                              <span className="item-name">{p.full_name?.toUpperCase()}</span>
                              <span className="item-meta">DNI: {p.dni || '---'} | Cel: {p.phone || '---'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="wizard-footer">
                    <button className="btn-cancel" onClick={onClose}>Cancelar</button>
                    <button 
                      className="btn-next" 
                      disabled={!sourcePatient} 
                      onClick={() => setStep(2)}
                    >
                      Continuar <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: Select Destination Patient (Main to keep) */}
              {step === 2 && (
                <div className="wizard-step">
                  <div className="step-instructions">
                    <h3>Paso 2: Selecciona el Paciente Principal (Se conservará)</h3>
                    <p>Busca y selecciona la ficha definitiva que deseas conservar. Todos los turnos e historias clínicas del duplicado se transferirán a este perfil.</p>
                  </div>

                  <form onSubmit={handleSearchDest} className="wizard-search-form">
                    <div className="search-input-wrapper">
                      <input 
                        type="text" 
                        placeholder="Buscar por Nombre o DNI del paciente definitivo..."
                        value={searchDest}
                        onChange={(e) => setSearchDest(e.target.value)}
                        autoFocus
                      />
                      <button type="submit" className="btn-search-trigger" disabled={loadingSearch || !searchDest.trim()}>
                        {loadingSearch ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
                      </button>
                    </div>
                  </form>

                  {destinationPatient ? (
                    <div className="selected-patient-preview destination card">
                      <div className="preview-badge green">FICHA PRINCIPAL SELECCIONADA</div>
                      <h4>{destinationPatient.full_name?.toUpperCase()}</h4>
                      <p>DNI: {destinationPatient.dni || 'Sin Documento'} | Celular: {destinationPatient.phone || 'Sin Celular'}</p>
                      <button className="btn-change-selection" onClick={() => setDestinationPatient(null)}>Cambiar Selección</button>
                    </div>
                  ) : (
                    <div className="search-results-box">
                      {destResults.length === 0 ? (
                        <div className="empty-results-text">Usa el buscador superior para encontrar pacientes.</div>
                      ) : (
                        <div className="search-list">
                          {destResults.map(p => (
                            <div key={p.id} className="search-list-item" onClick={() => setDestinationPatient(p)}>
                              <span className="item-name">{p.full_name?.toUpperCase()}</span>
                              <span className="item-meta">DNI: {p.dni || '---'} | Cel: {p.phone || '---'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="wizard-footer">
                    <button className="btn-back" onClick={() => setStep(1)}>Volver</button>
                    <button 
                      className="btn-next" 
                      disabled={!destinationPatient} 
                      onClick={() => setStep(3)}
                    >
                      Continuar <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: Comparison and Final Confirmation */}
              {step === 3 && (
                <div className="wizard-step">
                  <div className="step-instructions">
                    <h3>Paso 3: Confirma la Fusión de Registros</h3>
                    <p>Por favor, revisa detalladamente la comparación de perfiles antes de ejecutar la fusión. Esta acción **no se puede deshacer**.</p>
                  </div>

                  <div className="merge-comparison-grid">
                    {/* Source column */}
                    <div className="comparison-col source card">
                      <span className="label-col red">Ficha a Eliminar</span>
                      <h4>{sourcePatient?.full_name?.toUpperCase()}</h4>
                      <div className="comparison-fields">
                        <div className="field-group">
                          <label>DNI</label>
                          <span>{sourcePatient?.dni || '---'}</span>
                        </div>
                        <div className="field-group">
                          <label>Celular</label>
                          <span>{sourcePatient?.phone || '---'}</span>
                        </div>
                        <div className="field-group">
                          <label>Email</label>
                          <span>{sourcePatient?.email || '---'}</span>
                        </div>
                        <div className="field-group">
                          <label>Obra Social</label>
                          <span>{sourcePatient?.insurance_provider || 'Particular'}</span>
                        </div>
                      </div>
                    </div>

                    <div className="comparison-arrow">
                      <ArrowRight size={32} />
                    </div>

                    {/* Destination column */}
                    <div className="comparison-col destination card">
                      <span className="label-col green">Ficha a Conservar</span>
                      <h4>{destinationPatient?.full_name?.toUpperCase()}</h4>
                      <div className="comparison-fields">
                        <div className="field-group">
                          <label>DNI</label>
                          <span>{destinationPatient?.dni || sourcePatient?.dni || '---'} {(!destinationPatient?.dni && sourcePatient?.dni) && <span className="merged-tag">(Se importará)</span>}</span>
                        </div>
                        <div className="field-group">
                          <label>Celular</label>
                          <span>{destinationPatient?.phone || sourcePatient?.phone || '---'} {(!destinationPatient?.phone && sourcePatient?.phone) && <span className="merged-tag">(Se importará)</span>}</span>
                        </div>
                        <div className="field-group">
                          <label>Email</label>
                          <span>{destinationPatient?.email || sourcePatient?.email || '---'} {(!destinationPatient?.email && sourcePatient?.email) && <span className="merged-tag">(Se importará)</span>}</span>
                        </div>
                        <div className="field-group">
                          <label>Obra Social</label>
                          <span>{destinationPatient?.insurance_provider || sourcePatient?.insurance_provider || 'Particular'} {(!destinationPatient?.insurance_provider && sourcePatient?.insurance_provider) && <span className="merged-tag">(Se importará)</span>}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Transfer Summary */}
                  <div className="transfer-summary-box card">
                    <h5>Resumen de Transferencia:</h5>
                    <ul>
                      <li>Se reasignarán <strong>{stats.evolutions}</strong> evoluciones clínicas al paciente definitivo.</li>
                      <li>Se reasignarán <strong>{stats.appointments}</strong> turnos históricos y futuros al paciente definitivo.</li>
                      {sourcePatient?.medical_notes && (
                        <li>Se concatenarán las observaciones y antecedentes clínicos de la ficha eliminada.</li>
                      )}
                    </ul>
                  </div>

                  {/* Warning Panel */}
                  <div className="merge-warning-panel">
                    <AlertTriangle size={20} />
                    <span>
                      Al hacer clic en "CONFIRMAR FUSIÓN", el paciente duplicado se eliminará de forma permanente.
                    </span>
                  </div>

                  <div className="wizard-footer">
                    <button className="btn-back" disabled={merging} onClick={() => setStep(2)}>Volver</button>
                    <button 
                      className="btn-confirm-merge" 
                      disabled={merging} 
                      onClick={executeMerge}
                    >
                      {merging ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> PROCESANDO FUSIÓN...
                        </>
                      ) : (
                        'CONFIRMAR FUSIÓN'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default MergePatientsModal;
